import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as ForPersonIcon } from '../../../assets/icons/sharing/forPerson.svg';
import { ReactComponent as FromPersonIcon } from '../../../assets/icons/sharing/fromPerson.svg';
import { ReactComponent as SharedByIcon } from '../../../assets/icons/sharing/sharedBy.svg';
import DeleteActionButton from '../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../components/designe/ActionButtons/EditActionButton';
import AppTabs from '../../../components/designe/app-tabs/AppTabs';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import { successToastMessage } from '../../../components/layout/toast-root/toast';
import AppNavigate from '../../../components/reusable/app-navigate/AppNavigate';
import ClickableExercise from '../../../components/reusable/cliackable-exercise/ClickableExercise';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import SetOptionTable from '../../../components/reusable/set-options-table/SetOptionTable';
import { SetOptionTableMode } from '../../../components/reusable/set-options-table/interfaces';
import ViewField from '../../../components/reusable/view-field/ViewField';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import withRouter from '../../../hoc/with-router';
import { useUserSetPreferences } from '../../../hooks/use-user-set-preferences';
import { usePostPutProgramSet } from '../../../redux/api/programsAPI';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { Paths } from '../../../shared/paths';
import { IProgramSet } from '../../../types/interfaces/program-set';
import {
  getEmptyProgramSetFromProgramSet,
  getSetKey,
} from '../../../utils/training/set-utils';
import { getProgramItemViewSubtitle } from '../shared/program-items-list/program-item-list-item/program-list-subtitle';
import ProgramShareAccessTab from '../shared/program-share-access-tab/ProgramShareAccessTab';
import { useStyles } from './ProgramItem.styles';
import { useViewProgramItem } from './useViewProgramItem';

function ViewProgramItem(props) {
  const { params } = props;
  const { t } = useTranslation();
  const {
    programItem,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    onProgramItemDelete,
    programSetGroups,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useViewProgramItem(params.programItemId);
  const { navigate } = useNavigationStack();
  const [postPutProgramSet] = usePostPutProgramSet();
  const { data: user } = useGetMyDetails();
  const { showSaveSetButton } = useUserSetPreferences();
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);
  useRegisterPageContainerError({
    errors: isError && error ? [error] : [],
    isLoading: !programItem && isLoading,
    hasData: Boolean(programItem),
    onRetry: handleRetry,
  });

  const theme = useTheme();

  const { classes } = useStyles(theme);
  const handleEdit = useCallback(
    () => navigate(Paths.programs.toEditItemId(params.programItemId)),
    [navigate, params.programItemId],
  );

  const canConfigureAccess = !!programItem?.permissions?.canConfigureAccess;

  const viewSubtitle = useMemo(
    () => (programItem ? getProgramItemViewSubtitle(programItem, user) : null),
    [programItem, user],
  );

  const ViewSubtitleIcon =
    viewSubtitle?.icon === 'forPerson'
      ? ForPersonIcon
      : viewSubtitle?.icon === 'sharedBy'
        ? SharedByIcon
        : FromPersonIcon;

  const tabsConfig = useMemo(() => {
    if (!canConfigureAccess) {
      return [];
    }
    return [
      {
        label: t('programs.programItem'),
        path: Paths.programs.toViewItemId(params.programItemId),
      },
      {
        label: t('programs.accessTab'),
        path: Paths.programs.toViewItemAccessId(params.programItemId),
      },
    ];
  }, [canConfigureAccess, params.programItemId, t]);

  const onSaveSet = useCallback(
    (set: IProgramSet, title: string) => {
      const newProgramSet: IProgramSet = getEmptyProgramSetFromProgramSet(set);

      newProgramSet.title = title;
      newProgramSet.ownerId = user?.id;
      newProgramSet.setNumber = 0;
      newProgramSet.orderNumber = 0;
      newProgramSet.exercise = null;

      return postPutProgramSet({
        setId: undefined,
        body: newProgramSet,
      })
        .unwrap()
        .then((response) => {
          successToastMessage(t('setTable.setSaved'));
          return response;
        })
        .catch((e) => {
          console.log(e);
          return null;
        });
    },
    [postPutProgramSet, user, t],
  );

  const mainDetails = programItem && (
    <>
      <Box className={classes.headerContainer}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          className={classes.programItemHeader}>
          <Box flex={1} minWidth={0}>
            <Typography variant="h1" className={classes.programItemTitle}>
              {programItem?.title}
            </Typography>
            {viewSubtitle && (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{ color: 'primary.main' }}
                marginTop={0.5}>
                <ViewSubtitleIcon
                  style={{
                    width: 12,
                    height: 12,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="text-sm" color="primary" component="span">
                  {viewSubtitle.icon === 'forPerson'
                    ? t('trainings.listSubtitleForUser', {
                        name: viewSubtitle.label,
                      })
                    : t('trainings.listSubtitleFromUser', {
                        name: viewSubtitle.label,
                      })}
                </Typography>
              </Box>
            )}
            {programItem?.programCollection && (
              <Box marginTop={1}>
                <Typography variant="success-wrapped-sm">
                  {programItem?.programCollection?.title}
                </Typography>
              </Box>
            )}
          </Box>
          <Box display="flex" gap={0.5} flexShrink={0}>
            {programItem?.permissions?.canEdit && (
              <EditActionButton onClick={() => handleEdit()} />
            )}
            {programItem?.permissions?.canDelete && (
              <DeleteActionButton
                onClick={() => setShowDeleteConfirmation(true)}
              />
            )}
          </Box>
        </Box>
        {programItem?.description && (
          <ViewField
            value={programItem.description}
            label={t('common.description')}
          />
        )}
      </Box>

      {programSetGroups &&
        Object.values(programSetGroups).map((programSetGroup) => (
          <Box
            key={getSetKey(programSetGroup[0])}
            className={classes.groupWrapper}>
            {programSetGroup.map((set, setIndex) => (
              <React.Fragment key={set.id}>
                {setIndex > 0 && <Box className={classes.supersetDivider} />}
                <Box
                  key={set.id}
                  display={'flex'}
                  flexDirection="column"
                  gap={0.5}
                  className={classes.setWrapper}>
                  <ClickableExercise
                    exercise={set.exercise}
                    fromProgramId={params.programItemId}
                  />
                  <SetOptionTable
                    set={set}
                    mode={SetOptionTableMode.program}
                    showSetsColumn={true}
                    showRestColumn={true}
                    canSaveSet={showSaveSetButton}
                    onSaveSet={(title) => onSaveSet(set, title)}
                  />
                </Box>
              </React.Fragment>
            ))}
          </Box>
        ))}
    </>
  );

  return (
    <PageContainer
      title={`${params.programItemId ? t('programs.programItem') : t('programs.newProgramItem')}`}
      backButton>
      {programItem && (
        <>
          {canConfigureAccess && <AppTabs config={tabsConfig} />}
          <Box marginTop={canConfigureAccess ? 1 : 0}>
            <Routes>
              <Route index element={mainDetails} />
              {canConfigureAccess && (
                <Route
                  path="access"
                  element={
                    <ProgramShareAccessTab
                      programItem={programItem}
                      programItemId={params.programItemId}
                      isFetching={isFetching}
                      redirectTo={Paths.programs.toViewItemId(
                        params.programItemId,
                      )}
                    />
                  }
                />
              )}
              <Route
                path="*"
                element={
                  <AppNavigate
                    to={Paths.programs.toViewItemId(params.programItemId)}
                    replace
                  />
                }
              />
            </Routes>
          </Box>
        </>
      )}

      <ConfirmDialog
        open={!!showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(null)}
        title={t('common.delete')}
        onSubmit={() => {
          onProgramItemDelete();
        }}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
    </PageContainer>
  );
}

export default withRouter(ViewProgramItem);
